import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, EnvelopeSimple, CheckCircle, Leaf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex justify-center" data-testid="forgot-password-page">
      <div className="w-full max-w-md min-h-screen flex flex-col px-6 py-10 sm:px-8 sm:py-14">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-full bg-[#2B422F] flex items-center justify-center">
            <Leaf size={20} weight="fill" className="text-[#E6D4C4]" />
          </span>
          <div>
            <div className="font-serif-display text-2xl leading-none text-[#1A2421]">Tally</div>
            <div className="label-eyebrow mt-1">Carb Counter</div>
          </div>
        </div>

        <div className="mt-14">
          <span className="label-eyebrow">Reset password</span>
          <h1 className="font-serif-display text-4xl tracking-tight text-[#1A2421] mt-2">
            {sent ? "Check your inbox" : "Forgot password"}
          </h1>
          <p className="text-sm text-[#5C6B62] mt-2 leading-relaxed">
            {sent
              ? "If an account exists for that email, we just sent a reset link. The link expires in 30 minutes."
              : "Enter the email you signed up with and we'll send you a reset link."}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                className="input-organic"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                data-testid="forgot-password-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="forgot-password-submit"
            >
              {submitting ? "Sending…" : "Send reset link"}
              <EnvelopeSimple size={16} weight="bold" />
            </button>
          </form>
        ) : (
          <div className="mt-8 rounded-2xl bg-white border border-[#E6E3D8] p-5" data-testid="forgot-password-success">
            <div className="flex items-start gap-3">
              <CheckCircle size={22} weight="fill" className="text-[#2B422F] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-[#1A2421]">Email sent</div>
                <p className="text-sm text-[#5C6B62] mt-1 leading-relaxed">
                  Open the email titled <em>“Reset your Tally password”</em> and tap the button inside.
                  Don&apos;t see it? Check spam, or wait a minute and try again.
                </p>
              </div>
            </div>
          </div>
        )}

        <Link
          to="/login"
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#1A2421]"
          data-testid="forgot-password-back-to-login"
        >
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>

        <div className="mt-auto pt-10">
          <p className="text-xs text-[#5C6B62] text-center">
            Still stuck?{" "}
            <a
              href="mailto:tallycarbcounter@gmail.com?subject=Tally%20%C2%B7%20Password%20Reset%20Help"
              className="text-[#1A2421] underline underline-offset-4"
            >
              Email support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
